import {
  AndroidBiometricCryptoPreference,
  BiometricPermissionState,
  BrowserVault,
  Device,
  DeviceSecurityType,
  IdentityVaultConfig,
  Vault,
  VaultErrorCodes,
  VaultType,
} from '@ionic-enterprise/identity-vault';
import { AppPreferencesService } from '../../services';
import { AuthResult } from '@ionic-enterprise/auth';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { CONSTANTS } from '../../constants';
import { Capacitor } from '@capacitor/core';
import { Injectable, inject } from '@angular/core';
import { LogService, MessageBase } from '../../providers';
import { TokenResponse } from '../../models';
import { TranslateService } from '@ngx-translate/core';
import { UnlockMode } from './unlock-mode.type';
import { UserData } from '../user';
import { VaultFactory } from './vault.factory';
import { VaultServiceInterface } from './interfaces';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';

const LOCK_AFTER_BACKGROUNDED_MS = 2000;

@Injectable({ providedIn: 'root' })
export class VaultService implements VaultServiceInterface {
  private readonly appPreferences = inject(AppPreferencesService);
  private readonly lastUnlockMode: string = CONSTANTS.SAVE_LASTUNLOCKMODE_KEY;
  private readonly message = inject(MessageBase);
  private readonly sessionKey = CONSTANTS.AUTH_RESULT;
  private readonly translate = inject(TranslateService);
  private readonly userDataKey = CONSTANTS.USERDATA;
  private readonly vault: BrowserVault | Vault;

  private readonly lockedSubject: Subject<boolean> = new Subject();
  private readonly onLockCallback: (() => void) | undefined;
  private readonly vaultReadySubject = new BehaviorSubject<boolean>(false);

  constructor(vaultFactory: VaultFactory) {
    this.vault = vaultFactory.create();
  }

  public get vaultReady(): Observable<boolean> {
    return this.vaultReadySubject.asObservable();
  }

  public get locked(): Observable<boolean> {
    return this.lockedSubject.asObservable();
  }

  public async canUnlock(): Promise<boolean> {
    const isNeverLock = await this.neverLock();
    const isEmpty = await this.isEmpty();
    const isLocked = await this.vault.isLocked();

    return !isNeverLock && !isEmpty && isLocked;
  }

  public async clear(): Promise<void> {
    await this.vault.clear();
  }

  public async clearSession(): Promise<void> {
    await this.removeValue(this.sessionKey);
    await this.removeValue(this.userDataKey);
  }

  public async getLastUnlockMode(): Promise<UnlockMode> {
    return (await this.appPreferences.getValue(this.lastUnlockMode)) as UnlockMode;
  }

  public async getSession(): Promise<AuthResult | null> {
    try {
      return this.getValue<AuthResult | null>(this.sessionKey);
    } catch (error) {
      console.warn('Vault error getSession', error);
      return null;
    }
  }

  public async getUserData(): Promise<UserData | null> {
    try {
      return await this.getValue<UserData>(this.userDataKey);
    } catch (error) {
      console.warn('Vault error getUserData', error);
      throw error;
    }
  }

  public async getValue<T>(key: string): Promise<T | null> {
    try {
      return this.vault.getValue<T>(key);
    } catch (error) {
      console.warn('Vault error getValue', error);
      return null;
    }
  }

  public async hasDeviceSecurityEnabled(): Promise<boolean> {
    return (await Device.isSystemPasscodeSet()) || (await Device.isBiometricsEnabled());
  }

  public async init(appId = 'sinc.cases.ebeihilfe'): Promise<void> {
    if (this.vaultReadySubject.value) {
      return;
    }

    const lockMode = await this.appPreferences.getValue(this.lastUnlockMode);

    try {
      await this.vault.initialize({
        key: appId,
        type: VaultType.InMemory,
        deviceSecurityType: DeviceSecurityType.None,
        lockAfterBackgrounded: lockMode === 'NeverLock' ? undefined : LOCK_AFTER_BACKGROUNDED_MS,
        shouldClearVaultAfterTooManyFailedAttempts: true,
        androidBiometricsNegativeButtonText: this.translate.instant(_('biometric.androidBiometricsNegativeButtonText')),
        androidBiometricsPreferStrongVaultOrSystemPasscode: AndroidBiometricCryptoPreference.StrongVault,
        androidBiometricsPromptDescription: this.translate.instant(_('biometric.androidBiometricsPromptDescription')),
        androidBiometricsPromptSubtitle: this.translate.instant(_('biometric.androidBiometricsPromptSubtitle')),
        androidBiometricsPromptTitle: this.translate.instant(_('biometric.androidBiometricsPromptTitle')),
        iosBiometricsLocalizedCancelTitle: this.translate.instant(_('biometric.iosBiometricsLocalizedCancelTitle')),
        iosBiometricsLocalizedFallbackTitle: this.translate.instant(_('biometric.iosBiometricsLocalizedFallbackTitle')),
        iosBiometricsLocalizedReason: this.translate.instant(_('biometric.iosBiometricsLocalizedReason')),
      });

      this.vault.onLock(() => {
        this.onLockCallback?.();
        this.lockedSubject.next(true);
      });

      this.vault.onUnlock(() => this.lockedSubject.next(false));

      this.vault.onError((error) => {
        this.handleVaultError(error);
      });

      this.vaultReadySubject.next(true);
    } catch (error: unknown) {
      console.warn('Vault error init', error);
      await this.vault.clear();
      await this.updateUnlockMode('NeverLock');
      this.vaultReadySubject.next(true);
    }
  }

  public async isEmpty(): Promise<boolean> {
    const isNative = Capacitor.isNativePlatform();

    if (!isNative) {
      return false;
    }

    const isLocked = await this.vault.isLocked();

    if (isLocked) {
      return false;
    }

    return this.vault.isEmpty();
  }

  public async isLocked(): Promise<boolean> {
    const isLocked = await this.vault.isLocked();

    return this.vault.config?.type !== VaultType.InMemory && isLocked;
  }

  public async lockVault(): Promise<void> {
    await this.vault.lock();
  }

  public async neverLock(): Promise<boolean> {
    const lockMode = await this.appPreferences.getValue(this.lastUnlockMode);
    return lockMode == null || lockMode === 'NeverLock';
  }

  public async provision(): Promise<void> {
    if ((await Device.isBiometricsAllowed()) === BiometricPermissionState.Prompt) {
      try {
        await Device.showBiometricPrompt({ iosBiometricsLocalizedReason: 'Please authenticate to continue' });
      } catch (error) {
        console.warn('Vault error provision', error);
      }
    }
  }

  public async removeValue(key: string): Promise<void> {
    await this.vault.removeValue(key);
  }

  public async saveUserDataFromAuthResult(authResult: AuthResult): Promise<void> {
    const tokenResponse: TokenResponse = JSON.parse(authResult.rawResult);

    const previousUserData = await this.getUserData();

    const username = tokenResponse.userName ?? previousUserData?.username;
    const userSecret = tokenResponse.sec ?? previousUserData?.userSecret;
    const hasSecondFactor = tokenResponse.haspin ?? previousUserData?.hasSecondFactor ?? false;

    if (!userSecret) {
      throw new Error('User secret is missing');
    }

    return this.setValue<UserData>(this.userDataKey, {
      hasSecondFactor,
      username,
      userSecret,
    });
  }

  public async setLastUnlockMode(value: UnlockMode): Promise<void> {
    await this.appPreferences.setValue(this.lastUnlockMode, value);
  }

  public async setSession(value: AuthResult): Promise<void> {
    await this.setValue<AuthResult>(this.sessionKey, value);
  }

  public async setValue<T>(key: string, value: T): Promise<void> {
    await this.vault.setValue<T>(key, value);
  }

  public async toggleLockAfterBackgrounded(enabled: boolean): Promise<void> {
    const lockMode = await this.appPreferences.getValue(this.lastUnlockMode);
    if (lockMode === 'NeverLock') {
      return;
    }

    return this.vault.updateConfig({
      ...(this.vault.config as IdentityVaultConfig),
      lockAfterBackgrounded: enabled ? LOCK_AFTER_BACKGROUNDED_MS : undefined,
    });
  }

  public async unlockVault(): Promise<void> {
    await this.vault.unlock();
  }

  public async updateUnlockMode(unlockMode: UnlockMode): Promise<void> {
    let deviceSecurityType: DeviceSecurityType;
    let lockAfterBackgrounded: number | undefined = LOCK_AFTER_BACKGROUNDED_MS;
    let type: VaultType;

    switch (unlockMode) {
      case 'Device':
        await this.provision();
        deviceSecurityType = DeviceSecurityType.Both;
        type = VaultType.DeviceSecurity;
        break;

      case 'SystemPIN':
        deviceSecurityType = DeviceSecurityType.SystemPasscode;
        type = VaultType.DeviceSecurity;
        break;

      default:
        deviceSecurityType = DeviceSecurityType.None;
        lockAfterBackgrounded = undefined;
        type = VaultType.InMemory;
        break;
    }

    await this.setLastUnlockMode(unlockMode);

    return this.vault.updateConfig({
      ...(this.vault.config as IdentityVaultConfig),
      deviceSecurityType,
      lockAfterBackgrounded,
      type,
    });
  }

  private async handleVaultError(error: { code: VaultErrorCodes; message: string }): Promise<void> {
    console.warn('Vault error', error);

    switch (error.code) {
      case VaultErrorCodes.InvalidAuthMode:
      case VaultErrorCodes.Unknown:
        await this.sendMessage(_('vault.unknown_error'));
        return;

      case VaultErrorCodes.AndroidUnexpectedKeystoreError:
        await this.sendMessage(_('vault.android_unexpected_keystore_error'));
        return;

      case VaultErrorCodes.AndroidBiometricsLockedOut:
      case VaultErrorCodes.iOSBiometricsLockedOut:
        await this.sendMessage(_('vault.biometric_locked_out'));
        return;

      case VaultErrorCodes.KeyNotFound:
        await this.sendMessage(_('vault.key_not_found'));
        return;

      case VaultErrorCodes.InvalidatedCredential:
        await this.sendMessage(_('vault.invalidated_credential'));
        return;

      case VaultErrorCodes.TooManyFailedAttempts:
        await this.sendMessage(_('vault.too_many_failed_attempts'));
        return;

      case VaultErrorCodes.UserCanceledInteraction:
        await this.sendMessage(_('vault.user_canceled_interaction'));
        return;

      case VaultErrorCodes.VaultLocked:
        await this.unlockVault();
        return;

      case VaultErrorCodes.VaultUnavailable:
        await this.vault.clear();
        return;

      case VaultErrorCodes.SecurityNotAvailable:
        await this.sendMessage(_('general.text.error'));
        LogService.error(error.message);
        return;

      case VaultErrorCodes.AuthFailed:
        if (!(await Device.isBiometricsEnabled())) {
          await this.updateUnlockMode('SystemPIN');
          return;
        }

        if ((await Device.isBiometricsAllowed()) !== BiometricPermissionState.Granted) {
          await this.sendMessage(_('vault.biometric_usage_denied'));
          return;
        }

        await this.sendMessage(_('vault.auth_failed'));
        return;

      case VaultErrorCodes.InvalidArguments:
      case VaultErrorCodes.MismatchedPasscode:
      case VaultErrorCodes.MissingPasscode:
      case VaultErrorCodes.PasscodeNotEnabled:
        await this.sendMessage(_('general.text.error'));
        LogService.error(error.message);
        return;

      case VaultErrorCodes.BiometricsNotEnabled:
      case VaultErrorCodes.MissingBiometrics:
        await this.updateUnlockMode('SystemPIN');
        LogService.error(error.message);
        return;

      case VaultErrorCodes.AndroidSystemPasscodeUnavailable:
        await this.sendMessage(_('vault.android_system_passcode_unavailable'));
        await this.updateUnlockMode('NeverLock');
        return;
    }
  }

  private sendMessage(message: string): Promise<void> {
    return this.message.showError(message);
  }
}
